/**
 * Uso por pestaña / Academia / Tienda — Analytics → Producto.
 * RPC: get_product_engagement_analytics(p_days)
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';

export interface EngagementRow {
    key: string;
    visits: number;
    users: number;
    users_pct: number | null;
    dwell_p50_sec: number | null;
    dwell_avg_sec: number | null;
    group?: string;
}

export interface AcademyContentStat {
    items: number;
    users: number;
    users_pct: number | null;
    of_academy_pct: number | null;
}

export interface AcademySectionRow {
    key: string;
    visits: number;
    users: number;
    users_pct: number | null;
}

export interface StoreProductRow {
    kind: string;
    slug: string;
    name: string;
    category: string;
    views: number;
    users: number;
}

export interface StoreSectionRow {
    key: string;
    visits: number;
    users: number;
    users_pct: number | null;
}

export interface StoreProfileRow {
    key: string;
    active_users: number;
    visitors: number;
    visitors_pct: number | null;
}

export interface CaptureLoopStep {
    users: number;
    events: number;
    users_pct: number | null;
    of_creators_pct?: number | null;
    of_sharers_pct?: number | null;
}

export interface CaptureLoopMetrics {
    creators: CaptureLoopStep;
    sharers: CaptureLoopStep;
    chest_openers: CaptureLoopStep;
    throwers: CaptureLoopStep;
}

export interface ProductEngagementAnalytics {
    period_days: number;
    active_users: number;
    main_tabs: EngagementRow[];
    screens: EngagementRow[];
    academy: {
        visitors: number;
        visitors_pct: number | null;
        articles: AcademyContentStat;
        videos: AcademyContentStat;
        sections: AcademySectionRow[];
    };
    store: {
        visitors: number;
        visitors_pct: number | null;
        product_views: number;
        product_viewers: number;
        checkouts_started: number;
        checkouts_completed: number;
        sections: StoreSectionRow[];
        top_products: StoreProductRow[];
        by_experience_level: StoreProfileRow[];
        by_water_type: StoreProfileRow[];
    };
    capture_loop: CaptureLoopMetrics;
}

function asNumber(v: unknown, fallback = 0): number {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
    }
    return fallback;
}

function asNullableNumber(v: unknown): number | null {
    if (v == null) return null;
    const n = asNumber(v, Number.NaN);
    return Number.isFinite(n) ? n : null;
}

function parseEngagementRow(raw: unknown): EngagementRow | null {
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    if (typeof o.key !== 'string') return null;
    return {
        key: o.key,
        visits: asNumber(o.visits),
        users: asNumber(o.users),
        users_pct: asNullableNumber(o.users_pct),
        dwell_p50_sec: asNullableNumber(o.dwell_p50_sec),
        dwell_avg_sec: asNullableNumber(o.dwell_avg_sec),
        group: typeof o.group === 'string' ? o.group : undefined,
    };
}

function emptyContent(): AcademyContentStat {
    return { items: 0, users: 0, users_pct: null, of_academy_pct: null };
}

function parseContent(raw: unknown): AcademyContentStat {
    if (!raw || typeof raw !== 'object') return emptyContent();
    const o = raw as Record<string, unknown>;
    return {
        items: asNumber(o.items),
        users: asNumber(o.users),
        users_pct: asNullableNumber(o.users_pct),
        of_academy_pct: asNullableNumber(o.of_academy_pct),
    };
}

function parseAcademySection(raw: unknown): AcademySectionRow | null {
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    if (typeof o.key !== 'string') return null;
    return {
        key: o.key,
        visits: asNumber(o.visits),
        users: asNumber(o.users),
        users_pct: asNullableNumber(o.users_pct),
    };
}

function parseStoreProduct(raw: unknown): StoreProductRow | null {
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    return {
        kind: typeof o.kind === 'string' ? o.kind : 'unknown',
        slug: typeof o.slug === 'string' ? o.slug : '',
        name: typeof o.name === 'string' ? o.name : 'Producto',
        category: typeof o.category === 'string' ? o.category : 'sin_dato',
        views: asNumber(o.views),
        users: asNumber(o.users),
    };
}

function parseStoreSection(raw: unknown): StoreSectionRow | null {
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    if (typeof o.key !== 'string') return null;
    return {
        key: o.key,
        visits: asNumber(o.visits),
        users: asNumber(o.users),
        users_pct: asNullableNumber(o.users_pct),
    };
}

function emptyLoopStep(): CaptureLoopStep {
    return { users: 0, events: 0, users_pct: null, of_creators_pct: null, of_sharers_pct: null };
}

function parseLoopStep(raw: unknown): CaptureLoopStep {
    if (!raw || typeof raw !== 'object') return emptyLoopStep();
    const o = raw as Record<string, unknown>;
    return {
        users: asNumber(o.users),
        events: asNumber(o.events),
        users_pct: asNullableNumber(o.users_pct),
        of_creators_pct: asNullableNumber(o.of_creators_pct),
        of_sharers_pct: asNullableNumber(o.of_sharers_pct),
    };
}

function parseCaptureLoop(raw: unknown): CaptureLoopMetrics {
    const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    return {
        creators: parseLoopStep(o.creators),
        sharers: parseLoopStep(o.sharers),
        chest_openers: parseLoopStep(o.chest_openers),
        throwers: parseLoopStep(o.throwers),
    };
}

function parseProfileRow(raw: unknown): StoreProfileRow | null {
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    if (typeof o.key !== 'string') return null;
    return {
        key: o.key,
        active_users: asNumber(o.active_users),
        visitors: asNumber(o.visitors),
        visitors_pct: asNullableNumber(o.visitors_pct),
    };
}

export function parseProductEngagementAnalytics(raw: unknown): ProductEngagementAnalytics | null {
    if (raw == null) return null;
    let payload: unknown = raw;
    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload);
        } catch {
            return null;
        }
    }
    if (Array.isArray(payload)) payload = payload[0];
    if (!payload || typeof payload !== 'object') return null;
    const o = payload as Record<string, unknown>;
    const wrapped = o.get_product_engagement_analytics;
    if (wrapped && typeof wrapped === 'object') payload = wrapped;
    const p = payload as Record<string, unknown>;
    if (p.period_days == null || p.main_tabs == null) return null;

    const academyRaw = (p.academy && typeof p.academy === 'object' ? p.academy : {}) as Record<string, unknown>;
    const storeRaw = (p.store && typeof p.store === 'object' ? p.store : {}) as Record<string, unknown>;

    return {
        period_days: asNumber(p.period_days, 30),
        active_users: asNumber(p.active_users),
        main_tabs: Array.isArray(p.main_tabs)
            ? p.main_tabs.map(parseEngagementRow).filter((x): x is EngagementRow => x != null)
            : [],
        screens: Array.isArray(p.screens)
            ? p.screens.map(parseEngagementRow).filter((x): x is EngagementRow => x != null)
            : [],
        academy: {
            visitors: asNumber(academyRaw.visitors),
            visitors_pct: asNullableNumber(academyRaw.visitors_pct),
            articles: parseContent(academyRaw.articles),
            videos: parseContent(academyRaw.videos),
            sections: Array.isArray(academyRaw.sections)
                ? academyRaw.sections.map(parseAcademySection).filter((x): x is AcademySectionRow => x != null)
                : [],
        },
        store: {
            visitors: asNumber(storeRaw.visitors),
            visitors_pct: asNullableNumber(storeRaw.visitors_pct),
            product_views: asNumber(storeRaw.product_views),
            product_viewers: asNumber(storeRaw.product_viewers),
            checkouts_started: asNumber(storeRaw.checkouts_started),
            checkouts_completed: asNumber(storeRaw.checkouts_completed),
            sections: Array.isArray(storeRaw.sections)
                ? storeRaw.sections.map(parseStoreSection).filter((x): x is StoreSectionRow => x != null)
                : [],
            top_products: Array.isArray(storeRaw.top_products)
                ? storeRaw.top_products.map(parseStoreProduct).filter((x): x is StoreProductRow => x != null)
                : [],
            by_experience_level: Array.isArray(storeRaw.by_experience_level)
                ? storeRaw.by_experience_level.map(parseProfileRow).filter((x): x is StoreProfileRow => x != null)
                : [],
            by_water_type: Array.isArray(storeRaw.by_water_type)
                ? storeRaw.by_water_type.map(parseProfileRow).filter((x): x is StoreProfileRow => x != null)
                : [],
        },
        capture_loop: parseCaptureLoop(p.capture_loop),
    };
}

export function useProductEngagementAnalytics(days: number = 30) {
    return useQuery<ProductEngagementAnalytics>({
        queryKey: ['admin', 'product-engagement-analytics', days],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_product_engagement_analytics', {
                p_days: days,
            });
            if (error) throw error;
            const parsed = parseProductEngagementAnalytics(data);
            if (!parsed) {
                throw new Error('Respuesta inválida de get_product_engagement_analytics');
            }
            return parsed;
        },
        retry: 1,
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export const MAIN_TAB_LABELS: Record<string, string> = {
    board: 'Comunidad',
    rincon: 'Rincón',
    tienda: 'Tienda',
    profile: 'Perfil',
};

export const SCREEN_LABELS: Record<string, string> = {
    board: 'Comunidad (tablón)',
    rincon: 'Rincón (sin apartado)',
    'rincon-planificar': 'Rincón · Planificar',
    'rincon-comunidad': 'Rincón · Comunidad',
    'rincon-comunidad-general': 'Rincón · Comunidad · General',
    'rincon-comunidad-clubes': 'Rincón · Comunidad · Clubes',
    'rincon-comunidad-grupos': 'Rincón · Comunidad · Grupos',
    'rincon-comunidad-eventos': 'Rincón · Comunidad · Eventos',
    'rincon-comunidad-salidas': 'Rincón · Comunidad · Salidas',
    'rincon-academia': 'Rincón · Academia',
    'rincon-academia-atlas': 'Academia · Atlas',
    'rincon-academia-viajes': 'Academia · Viajes',
    'rincon-tienda': 'Rincón · Tienda (legacy)',
    tienda: 'Tienda',
    'store-wallet': 'Tienda · Cartera',
    'store-my-orders': 'Tienda · Mis pedidos',
    'store-order-success': 'Tienda · Pedido OK',
    'store-order-cancelled': 'Tienda · Pedido cancelado',
    profile: 'Perfil',
    pescadillo: 'Pescadillo',
    'intel-ia': 'Intel IA',
    'intel-pescadillo-chat': 'Chat Pescadillo',
    'planificar-salida': 'Planificar salida',
    'fishing-planner': 'Planificador',
};

export const ACADEMY_SECTION_LABELS: Record<string, string> = {
    general: 'General',
    atlas: 'Atlas',
    viajes: 'Viajes',
    embarcacion: 'Embarcación',
    modality: 'Modalidad',
    explore: 'Explorar',
};

export const STORE_SECTION_LABELS: Record<string, string> = {
    all: 'General',
    equipamiento: 'Embarcación',
    canas: 'Cañas',
    carretes: 'Carretes',
    senuelos: 'Señuelos',
    ropa: 'Ropa',
    accesorios: 'Accesorios',
    pro: 'Pro',
    colaboradores: 'Colaboradores',
};

export const EXPERIENCE_LABELS: Record<string, string> = {
    principiante: 'Principiante',
    intermedio: 'Intermedio',
    experto: 'Experto',
    sin_dato: 'Sin dato',
};

export const WATER_LABELS: Record<string, string> = {
    dulce: 'Agua dulce',
    salada: 'Agua salada',
    ambas: 'Ambas',
    sin_dato: 'Sin dato',
};

export function labelForScreen(key: string): string {
    if (SCREEN_LABELS[key]) return SCREEN_LABELS[key];
    if (key.startsWith('rincon-')) return key.replace('rincon-', 'Rincón · ');
    return key;
}
